/**
 * Reading and changing forms, assignments and submissions.
 *
 * The catalog, a person's assignments, the immutable version a person
 * answers, and every write go through the callables. Two things are read
 * directly, because the rules allow it and no callable serves them: the
 * form document itself (a draft has no version yet, and the builder needs
 * its fields), and — for administrators — everyone's assignments and the
 * submissions for one form.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs, getFirestore, query, where } from 'firebase/firestore';
import { app } from '../../lib/firebase';
import { call } from '../../lib/callable';
import { toIsoDateTime } from '../../shared/dates';
import {
  toAssignmentStatus, toAudience, toFieldType, toStatus, toFieldDef,
  type AnswerValue, type Assignment, type FieldDef, type FormFields, type FormRecord, type FormSummary,
  type FormVersion, type Submission, toCategory } from './types';

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const when = (v: unknown): string | null => {
  if (typeof v === 'string') return v || null;
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    const d = (v as { toDate: () => Date }).toDate();
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return null;
};

export function toField(d: Record<string, unknown>): FieldDef {
  const rules = typeof d.validationRules === 'object' && d.validationRules !== null ? (d.validationRules as Record<string, unknown>) : null;
  const r: FieldDef['validationRules'] = rules ? {} : null;
  if (rules && r) {
    for (const k of ['minLength', 'maxLength', 'minValue', 'maxValue'] as const) { const v = num(rules[k]); if (v !== null) r[k] = v; }
    if (typeof rules.regex === 'string' && rules.regex) r.regex = rules.regex;
  }
  return {
    id: String(d.id ?? ''), type: toFieldType(d.type), label: str(d.label) ?? '', description: str(d.description),
    helpText: str(d.helpText), required: d.required === true, options: strs(d.options), validationRules: r,
  };
}

function toSummary(d: Record<string, unknown>): FormSummary {
  return {
    id: String(d.id ?? ''), title: str(d.title) ?? '(untitled)', status: toStatus(d.status),
    departmentIds: strs(d.departmentIds), currentVersionNumber: num(d.currentVersionNumber), updatedAt: when(d.updatedAt),
  };
}

export function toRecord(id: string, d: Record<string, unknown>): FormRecord {
  return {
    ...toSummary({ ...d, id }),
    description: str(d.description), instructions: str(d.instructions),
    fields: Array.isArray(d.fields) ? (d.fields as Record<string, unknown>[]).map(toField) : [],
    targetAudience: toAudience(d.targetAudience), targetDepartmentIds: strs(d.targetDepartmentIds),
    targetRoles: strs(d.targetRoles), targetUserIds: strs(d.targetUserIds),
    requiresLegalAttestation: d.requiresLegalAttestation !== false,
    legalAttestationText: str(d.legalAttestationText) ?? '',
    formCategory: toCategory(d.formCategory),
    renewalIntervalMonths: num(d.renewalIntervalMonths),
    currentVersionId: str(d.currentVersionId), createdAt: when(d.createdAt),
    publishedAt: when(d.publishedAt), retiredAt: when(d.retiredAt),
  };
}

export function toAssignment(d: Record<string, unknown>): Assignment {
  return {
    id: String(d.id ?? ''), formId: str(d.formId) ?? '', formVersionId: str(d.formVersionId) ?? '',
    formVersionNumber: num(d.formVersionNumber) ?? 0, formTitle: str(d.formTitle) ?? '',
    userId: str(d.userId) ?? '', userEmail: str(d.userEmail), userDisplayName: str(d.userDisplayName),
    status: toAssignmentStatus(d.status), dueDate: when(d.dueDate), assignedAt: when(d.assignedAt),
    completedAt: when(d.completedAt), waivedAt: when(d.waivedAt), waivedReason: str(d.waivedReason),
    submissionId: str(d.submissionId),
  };
}

export function toSubmission(id: string, d: Record<string, unknown>): Submission {
  return {
    id, assignmentId: str(d.assignmentId) ?? '', formId: str(d.formId) ?? '', formVersionNumber: num(d.formVersionNumber) ?? 0,
    userId: str(d.userId) ?? '', userEmail: str(d.userEmail), userDisplayName: str(d.userDisplayName),
    submittedAt: when(d.submittedAt), legalAttestationTextSnapshot: str(d.legalAttestationTextSnapshot) ?? '',
    responses: Array.isArray(d.responses) ? (d.responses as Record<string, unknown>[]).map((r) => ({
      fieldId: String(r.fieldId ?? ''), type: toFieldType(r.type), labelSnapshot: str(r.labelSnapshot) ?? '',
      requiredSnapshot: r.requiredSnapshot === true,
      value: typeof r.value === 'string' || typeof r.value === 'number' || typeof r.value === 'boolean' ? r.value : null,
    })) : [],
  };
}

export const formsKey = (orgId: string | null) => ['forms', orgId] as const;
export const formKey = (orgId: string | null, id: string | null) => ['form', orgId, id] as const;
export const versionKey = (orgId: string | null, formId: string | null, versionId: string | null) => ['form-version', orgId, formId, versionId] as const;
export const myFormAssignmentsKey = (orgId: string | null, uid: string | null) => ['my-attestations', orgId, uid] as const;
export const formAssignmentsKey = (orgId: string | null, id: string | null) => ['form-assignments', orgId, id] as const;
export const formSubmissionsKey = (orgId: string | null, id: string | null) => ['form-submissions', orgId, id] as const;

export function useForms(orgId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: formsKey(orgId),
    enabled: Boolean(orgId) && enabled,
    queryFn: async () => (await call<Record<string, never>, { forms: Record<string, unknown>[] }>('listForms', {})).forms.map(toSummary),
  });
}

export function useForm(orgId: string | null, formId: string | null, enabled = true) {
  return useQuery({
    queryKey: formKey(orgId, formId),
    enabled: Boolean(orgId && formId) && enabled,
    queryFn: async (): Promise<FormRecord | null> => {
      const snap = await getDoc(doc(getFirestore(app), 'orgs', orgId!, 'forms', formId!));
      return snap.exists() ? toRecord(snap.id, snap.data() as Record<string, unknown>) : null;
    },
  });
}

export function useFormVersion(orgId: string | null, formId: string | null, versionId: string | null) {
  return useQuery({
    queryKey: versionKey(orgId, formId, versionId),
    enabled: Boolean(orgId && formId && versionId),
    queryFn: async (): Promise<FormVersion> => {
      const res = await call<{ formId: string; versionId: string }, { version: Record<string, unknown> }>('getFormVersion', { formId: formId!, versionId: versionId! });
      const v = res.version ?? {};
      const s = (typeof v.snapshot === 'object' && v.snapshot !== null ? v.snapshot : {}) as Record<string, unknown>;
      return {
        id: String(v.id ?? ''), versionNumber: num(v.versionNumber) ?? 0, publishedAt: when(v.publishedAt),
        title: str(s.title) ?? '', description: str(s.description), instructions: str(s.instructions),
        fields: Array.isArray(s.fields) ? (s.fields as Record<string, unknown>[]).map(toField) : [],
        requiresLegalAttestation: s.requiresLegalAttestation !== false,
        legalAttestationText: str(s.legalAttestationText) ?? '',
      };
    },
  });
}

export function useMyFormAssignments(orgId: string | null, uid: string | null, enabled: boolean) {
  return useQuery({
    queryKey: myFormAssignmentsKey(orgId, uid),
    enabled: Boolean(orgId && uid) && enabled,
    queryFn: async () => (await call<Record<string, never>, { assignments: Record<string, unknown>[] }>('listMyAssignments', {})).assignments.map(toAssignment),
  });
}

export function useFormAssignments(orgId: string | null, formId: string | null, isAdmin: boolean) {
  return useQuery({
    queryKey: formAssignmentsKey(orgId, formId),
    enabled: Boolean(orgId && formId) && isAdmin,
    queryFn: async () => {
      const snap = await getDocs(query(collection(getFirestore(app), 'orgs', orgId!, 'attestationAssignments'), where('formId', '==', formId)));
      return snap.docs.map((d) => toAssignment({ ...d.data(), id: d.id }));
    },
  });
}

export function useFormSubmissions(orgId: string | null, formId: string | null, isAdmin: boolean) {
  return useQuery({
    queryKey: formSubmissionsKey(orgId, formId),
    enabled: Boolean(orgId && formId) && isAdmin,
    queryFn: async () => {
      const snap = await getDocs(query(collection(getFirestore(app), 'orgs', orgId!, 'attestations'), where('formId', '==', formId)));
      return snap.docs.map((d) => toSubmission(d.id, d.data() as Record<string, unknown>));
    },
  });
}

/** One submission — the person's own, or any for an administrator. */
export function useSubmission(orgId: string | null, submissionId: string | null) {
  return useQuery({
    queryKey: ['attestation', orgId, submissionId],
    enabled: Boolean(orgId && submissionId),
    queryFn: async (): Promise<Submission | null> => {
      const snap = await getDoc(doc(getFirestore(app), 'orgs', orgId!, 'attestations', submissionId!));
      return snap.exists() ? toSubmission(snap.id, snap.data() as Record<string, unknown>) : null;
    },
  });
}

export function toPayload(f: FormFields): Record<string, unknown> {
  return {
    title: f.title.trim(),
    description: f.description.trim(),
    instructions: f.instructions.trim(),
    fields: f.fields.map(toFieldDef),
    targetAudience: f.targetAudience,
    targetDepartmentIds: f.targetAudience === 'departments' ? f.targetDepartmentIds : [],
    targetRoles: f.targetAudience === 'roles' ? f.targetRoles : [],
    targetUserIds: f.targetAudience === 'users' ? f.targetUserIds : [],
    requiresLegalAttestation: f.requiresLegalAttestation,
    legalAttestationText: f.legalAttestationText.trim(),
    formCategory: f.formCategory,
    renewalIntervalMonths: f.renewalIntervalMonths.trim() === '' ? null : Number(f.renewalIntervalMonths),
  };
}

function useInvalidate(orgId: string | null, id: string | null) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: formsKey(orgId) });
    if (id) {
      void qc.invalidateQueries({ queryKey: formKey(orgId, id) });
      void qc.invalidateQueries({ queryKey: formAssignmentsKey(orgId, id) });
      void qc.invalidateQueries({ queryKey: formSubmissionsKey(orgId, id) });
    }
    void qc.invalidateQueries({ queryKey: ['my-attestations', orgId] });
  };
}

export function useCreateForm(orgId: string | null) {
  const done = useInvalidate(orgId, null);
  return useMutation({
    mutationFn: async (f: FormFields) => (await call<unknown, { formId: string }>('createForm', toPayload(f))).formId,
    onSuccess: done,
  });
}

export function useUpdateForm(orgId: string | null, id: string) {
  const done = useInvalidate(orgId, id);
  return useMutation({
    mutationFn: async (f: FormFields) => { await call<unknown, unknown>('updateForm', { formId: id, ...toPayload(f) }); },
    onSuccess: done,
  });
}

export type FormAction =
  | { name: 'publishForm'; dueDaysDefault: string; dueDateOverride: string }
  | { name: 'retireForm'; reason: string }
  | { name: 'waiveAssignment'; assignmentId: string; reason: string };

export interface PublishResult { versionNumber?: number; assignmentCount?: number; skipped?: boolean }

export function actionPayload(formId: string, a: FormAction): Record<string, unknown> {
  switch (a.name) {
    case 'publishForm': {
      const p: Record<string, unknown> = { formId };
      const iso = toIsoDateTime(a.dueDateOverride);
      if (iso) p.dueDateOverride = iso;
      else if (a.dueDaysDefault.trim() && Number.isInteger(Number(a.dueDaysDefault))) p.dueDaysDefault = Number(a.dueDaysDefault);
      return p;
    }
    case 'retireForm': {
      const p: Record<string, unknown> = { formId };
      if (a.reason.trim()) p.reason = a.reason.trim();
      return p;
    }
    case 'waiveAssignment':
      return { assignmentId: a.assignmentId, reason: a.reason.trim() };
  }
}

export function useFormAction(orgId: string | null, formId: string) {
  const done = useInvalidate(orgId, formId);
  return useMutation({
    mutationFn: async (a: FormAction) => call<unknown, PublishResult>(a.name, actionPayload(formId, a)),
    onSuccess: done,
  });
}

export function useSubmitAttestation(orgId: string | null, uid: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { assignmentId: string; responses: { fieldId: string; value: AnswerValue }[] }) => {
      const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent.slice(0, 500);
      const p: Record<string, unknown> = { assignmentId: input.assignmentId, responses: input.responses };
      if (ua) p.userAgent = ua;
      return call<unknown, { success: boolean; attestationId: string }>('submitAttestation', p);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: myFormAssignmentsKey(orgId, uid) }),
  });
}
